import { useEffect, useRef, useCallback } from 'react';
import { useUIContext } from '../contexts/UIContext';
import logger from '../utils/logger';

export const useSwipeNavigation = () => {
  const { page, setPage } = useUIContext();
  const touchStartRef = useRef(null);
  const isSwipeActiveRef = useRef(false);

  // Detect if running as PWA
  const isPWA = window.matchMedia('(display-mode: standalone)').matches || 
                 window.navigator.standalone === true;
  
  // Minimum distance for a swipe gesture (shorter in PWA for easier activation)
  const minSwipeDistance = isPWA ? 30 : 50;
  
  // Maximum distance from edge to trigger swipe
  // Make edge threshold larger in PWA mode for easier gesture activation
  const edgeThreshold = isPWA ? 50 : 20;
  
  // Maximum vertical distance to consider it a horizontal swipe
  const maxVerticalDistance = 100;

  // Smart page mapping - determines where to go back to based on current page
  const getPreviousPage = useCallback((currentPage) => {
    const pageMap = {
      // Learning flow pages
      'lesson': 'learning_path',
      'quiz': 'learning_path',
      'quiz_results': 'learning_path',
      'practice': 'learning_path',
      
      // Dashboard flow pages
      'game_zone': 'dashboard',
      'sticker_book': 'dashboard',
      'storybook': 'dashboard',
      'profile': 'dashboard',
      
      // Parent pages
      'settings': 'dashboard',
      'parental_auth': 'dashboard',
      'notifications': 'dashboard',
      
      // Settings and support pages
      'profile_picker': 'dashboard',
      'add_new_profile': 'dashboard',
      'forgot_password': 'dashboard',
      'support_page': 'dashboard',
      'subscription_page': 'dashboard',
      'post_subscribe_onboarding': 'dashboard',
      'trial_complete_transition': 'dashboard',
      'unsubscribe': 'dashboard',
      'analytics_dashboard': 'dashboard',
      
      // Activity pages
      'categorySort_activity': 'learning_path',
      'colorMatch_activity': 'learning_path',
      'listenFind_activity': 'learning_path',
      'numberMatch_activity': 'learning_path',
      'sentenceScramble_activity': 'learning_path',
      'whatAmI_activity': 'learning_path',
      'simpleConversation_activity': 'learning_path',
      
      // Special cases
      'learning_path': 'dashboard',
      'dashboard': null, // Can't go back from dashboard
      'onboarding': null, // Can't go back from onboarding
      'loading': null, // Can't go back from loading
      'modal_test': 'dashboard'
    };
    
    return pageMap[currentPage] || 'dashboard';
  }, []);

  // Haptic feedback function
  const triggerHapticFeedback = useCallback((type = 'light') => {
    if (typeof window !== 'undefined' && 'vibrate' in navigator) {
      switch (type) {
        case 'light':
          navigator.vibrate(10);
          break;
        case 'medium':
          navigator.vibrate(20);
          break;
        case 'heavy':
          navigator.vibrate([10, 10, 10]);
          break;
        default:
          navigator.vibrate(10);
      }
    }
  }, []);

  // Handle touch start
  const handleTouchStart = useCallback((e) => {
    const touch = e.touches[0];
    const startX = touch.clientX;
    const startY = touch.clientY;
    const screenWidth = window.innerWidth;

    // Check if touch started near the left edge (for back swipe)
    const isNearLeftEdge = startX <= edgeThreshold;
    const isNearRightEdge = startX >= screenWidth - edgeThreshold;

    if (isNearLeftEdge || isNearRightEdge) {
      logger.info('SwipeNavigation', 'Info', startX, 'threshold:', edgeThreshold, 'isPWA:', isPWA, 'isLeftEdge:', isNearLeftEdge, 'isRightEdge:', isNearRightEdge);
      touchStartRef.current = {
        x: startX,
        y: startY,
        time: Date.now(),
        isLeftEdge: isNearLeftEdge,
        isRightEdge: isNearRightEdge
      };
      isSwipeActiveRef.current = true;
      
      // CRITICAL: Prevent default browser behavior immediately
      e.preventDefault();
      e.stopPropagation();
      e.stopImmediatePropagation();
      
      // Prevent browser back gesture for ALL edge touches
      document.body.style.overscrollBehavior = 'none';
      document.body.style.touchAction = 'none';
      document.documentElement.style.overscrollBehavior = 'none';
      document.documentElement.style.touchAction = 'none';
      
      // In PWA mode, also prevent any default navigation
      if (isPWA && e.cancelable) {
        e.returnValue = false;
      }
    }
  }, [edgeThreshold, isPWA]);

  // Handle touch move
  const handleTouchMove = useCallback((e) => {
    if (!isSwipeActiveRef.current || !touchStartRef.current) return;

    const touch = e.touches[0];
    const currentX = touch.clientX;
    const currentY = touch.clientY;
    const deltaX = currentX - touchStartRef.current.x;
    const deltaY = Math.abs(currentY - touchStartRef.current.y);

    // If vertical movement is too much, cancel the swipe
    if (deltaY > maxVerticalDistance) {
      isSwipeActiveRef.current = false;
      touchStartRef.current = null;
      return;
    }

    // For left edge swipe (going right), we want positive deltaX
    // For right edge swipe (going left), we want negative deltaX
    const { isLeftEdge, isRightEdge } = touchStartRef.current;
    
    // CRITICAL: Always prevent default browser behavior during swipe
    e.preventDefault();
    e.stopPropagation();
    e.stopImmediatePropagation();
    
    if (isLeftEdge && deltaX > 0) {
      // Left edge swipe right - this is a back gesture
      // Add smooth visual feedback during swipe
      document.body.style.transform = `translate3d(${deltaX * 0.3}px, 0, 0)`;
      document.body.style.transition = 'none';
    } else if (isRightEdge && deltaX < 0) {
      // Right edge swipe left - this could be a forward gesture (future feature)
      // Just prevent default for now
    }
  }, []);

  // Handle touch end
  const handleTouchEnd = useCallback((e) => {
    if (!isSwipeActiveRef.current || !touchStartRef.current) return;

    const touch = e.changedTouches[0];
    const endX = touch.clientX;
    const endY = touch.clientY;
    const deltaX = endX - touchStartRef.current.x;
    const deltaY = Math.abs(endY - touchStartRef.current.y);
    const deltaTime = Date.now() - touchStartRef.current.time;

    // Reset swipe state
    isSwipeActiveRef.current = false;
    const { isLeftEdge, isRightEdge } = touchStartRef.current;
    touchStartRef.current = null;

    // Check if this qualifies as a swipe gesture
    const isHorizontalSwipe = Math.abs(deltaX) > Math.abs(deltaY);
    const isLongEnough = Math.abs(deltaX) >= minSwipeDistance;
    const isFastEnough = deltaTime < 500; // 500ms max duration

    logger.info('SwipeNavigation', 'Info', {
      isHorizontalSwipe,
      isLongEnough,
      isFastEnough,
      deltaX,
      deltaY,
      deltaTime,
      isLeftEdge,
      isRightEdge
    });

    if (isHorizontalSwipe && isLongEnough && isFastEnough) {
      if ((isLeftEdge && deltaX > 0) || (isRightEdge && deltaX < 0)) {
        // Both left edge swipe right AND right edge swipe left - go back
        logger.info('SwipeNavigation', 'Info', deltaX, 'deltaY:', deltaY, 'deltaTime:', deltaTime);
        triggerHapticFeedback('medium');
        
        // Use smart page mapping to determine where to go back to
        const previousPage = getPreviousPage(page);
        logger.info('SwipeNavigation', 'Info', page, 'Previous page:', previousPage);
        
        if (previousPage) {
          logger.info('SwipeNavigation', 'Info', previousPage);
          setPage(previousPage);
          logger.info('SwipeNavigation', 'Info');
        } else {
          logger.info('SwipeNavigation', 'Info', page);
        }
        
        // Add smooth transition for visual feedback AFTER navigation
        document.body.style.transition = 'transform 0.3s cubic-bezier(0.4, 0, 0.2, 1)';
        document.body.style.transform = 'translate3d(100%, 0, 0)';
        
        // Reset transform after animation
        setTimeout(() => {
          document.body.style.transform = 'translate3d(0, 0, 0)';
          document.body.style.transition = '';
        }, 150);
        
        e.preventDefault();
        e.stopPropagation();
        e.stopImmediatePropagation();
        
        // Keep gesture prevention active - DO NOT reset styles
        // This prevents browser native gestures from being re-enabled
        
        return false;
      }
    } else {
      // Reset transform if swipe doesn't qualify
      document.body.style.transition = 'transform 0.2s cubic-bezier(0.4, 0, 0.2, 1)';
      document.body.style.transform = 'translate3d(0, 0, 0)';
      setTimeout(() => {
        document.body.style.transition = '';
      }, 200);
    }
  }, [page, getPreviousPage, setPage, triggerHapticFeedback]);

  // Handle touch cancel
  const handleTouchCancel = useCallback(() => {
    isSwipeActiveRef.current = false;
    touchStartRef.current = null;
    
    // Reset transform on cancel
    document.body.style.transition = 'transform 0.2s cubic-bezier(0.4, 0, 0.2, 1)';
    document.body.style.transform = 'translate3d(0, 0, 0)';
    
    // Keep gesture prevention active - DO NOT reset styles
    // This prevents browser native gestures from being re-enabled
    
    setTimeout(() => {
      document.body.style.transition = '';
    }, 200);
  }, []);

  // Global edge touch prevention - prevents browser native gestures
  const preventBrowserGestures = useCallback((e) => {
    const touch = e.touches[0];
    const startX = touch.clientX;
    const screenWidth = window.innerWidth;
    const isNearLeftEdge = startX <= edgeThreshold;
    const isNearRightEdge = startX >= screenWidth - edgeThreshold;

    if (isNearLeftEdge || isNearRightEdge) {
      logger.info('SwipeNavigation', 'Info', startX, 'threshold:', edgeThreshold);
      // Aggressively prevent browser gestures for edge touches
      e.preventDefault();
      e.stopPropagation();
      e.stopImmediatePropagation();
      
      // Set global styles to prevent browser gestures
      document.body.style.overscrollBehavior = 'none';
      document.body.style.touchAction = 'none';
      document.documentElement.style.overscrollBehavior = 'none';
      document.documentElement.style.touchAction = 'none';
      
      // In PWA mode, also prevent any default navigation
      if (isPWA && e.cancelable) {
        e.returnValue = false;
      }
    }
  }, [edgeThreshold, isPWA]);

  // Set up touch event listeners
  useEffect(() => {
    logger.info('SwipeNavigation', 'Info', isPWA, 'edgeThreshold:', edgeThreshold);
    
    // Use window instead of document.body for better event capture
    const element = window;
    const bodyElement = document.body;
    
    // Add performance optimization for smooth animations
    bodyElement.style.willChange = 'transform';
    
    // CRITICAL: Add listeners to window with capture phase for earliest possible interception
    // This ensures we catch events before any other handlers
    element.addEventListener('touchstart', preventBrowserGestures, { passive: false, capture: true });
    element.addEventListener('touchstart', handleTouchStart, { passive: false, capture: true });
    element.addEventListener('touchmove', handleTouchMove, { passive: false, capture: true });
    element.addEventListener('touchend', handleTouchEnd, { passive: false, capture: true });
    element.addEventListener('touchcancel', handleTouchCancel, { passive: false, capture: true });

    // Also add to document for redundancy
    document.addEventListener('touchstart', preventBrowserGestures, { passive: false, capture: true });
    document.addEventListener('touchstart', handleTouchStart, { passive: false, capture: true });

    return () => {
      element.removeEventListener('touchstart', preventBrowserGestures, { capture: true });
      element.removeEventListener('touchstart', handleTouchStart, { capture: true });
      element.removeEventListener('touchmove', handleTouchMove, { capture: true });
      element.removeEventListener('touchend', handleTouchEnd, { capture: true });
      element.removeEventListener('touchcancel', handleTouchCancel, { capture: true });
      
      document.removeEventListener('touchstart', preventBrowserGestures, { capture: true });
      document.removeEventListener('touchstart', handleTouchStart, { capture: true });
      
      // Clean up performance optimization
      bodyElement.style.willChange = '';
      bodyElement.style.transform = '';
      bodyElement.style.transition = '';
    };
  }, [preventBrowserGestures, handleTouchStart, handleTouchMove, handleTouchEnd, handleTouchCancel, isPWA, edgeThreshold]);

  return {
    triggerHapticFeedback
  };
};
